/**
 * Centralised "logical parent" map for every route in the app.
 *
 * The browser's history stack is unreliable for a back-arrow:
 *  - Deep links land users on inner pages with no history.
 *  - Push notifications / cold starts wipe the stack.
 *  - Settings sub-pages used to loop into each other because
 *    `navigate(-1)` just replayed the previous push.
 *
 * Instead, every screen declares the page that should logically
 * appear when the user taps "back". `resolveBackPath(pathname, user)`
 * walks this map (supporting `:param` placeholders) and falls back to
 * the user's role-home if the current path is unmapped.
 *
 * Pages that are entry/root of a section map to `null` so the back
 * arrow takes the user one level up to their role-home.
 */

const PARENTS = {
  // === GUEST ===
  '/guest/home': null,
  '/guest/venues': '/guest/home',
  '/guest/qr': '/guest/home',
  '/guest/share': '/guest/home',
  '/guest/chat/:convoId': '/guest/share',
  '/guest/profile': '/guest/home',
  '/guest/history': '/guest/settings',
  '/guest/settings': '/guest/profile',
  '/guest/settings/personal': '/guest/settings',
  '/guest/settings/security': '/guest/settings',
  '/guest/settings/privacy': '/guest/settings',
  '/guest/settings/notifications': '/guest/settings',
  '/guest/settings/payment-method': '/guest/settings',
  '/guest/payment-method/callback': '/guest/settings/payment-method',
  '/guest/ratings': '/guest/settings',
  '/guest/rate/:transactionId': '/guest/ratings',
  '/guest/payment/:txnId': '/guest/home',
  '/guest/tips/:guestPassId': '/guest/home',
  '/guest/transaction-complete': '/guest/home',
  '/invite/:inviteId': null,
  '/invite/:inviteId/waiting': '/guest/home',

  // === HOST ===
  '/host/active-qr': null,
  '/host/generate-qr': '/host/active-qr',
  '/host/listings': '/host/active-qr',
  '/host/earnings': '/host/active-qr',
  '/host/earnings/tips': '/host/earnings',
  '/host/tips/:tipId': '/host/earnings/tips',
  '/host/messages': '/host/active-qr',
  '/host/chat/:convoId': '/host/messages',
  '/host/profile': '/host/active-qr',
  '/host/referrals': '/host/profile',
  '/host/refer-venue': '/host/referrals',
  '/host/invites/:inviteId/pending': '/host/active-qr',
  '/host/settings': '/host/profile',
  '/host/settings/personal': '/host/settings',
  '/host/settings/security': '/host/settings',
  '/host/settings/privacy': '/host/settings',
  '/host/settings/notifications': '/host/settings',
  '/host/settings/payouts': '/host/settings',
  '/host/settings/payouts/history': '/host/settings/payouts',
  '/host/settings/payouts/statements/:batchId': '/host/settings/payouts/history',
  '/host/dashboard': null,
  '/host-invite/:inviteId': null,

  // === VENUE OWNER ===
  '/venue/analytics': null,
  '/venue/people': '/venue/analytics',
  '/venue/scan': '/venue/analytics',
  '/venue/share': '/venue/analytics',
  '/venue/chat/:convoId': '/venue/share',
  '/venue/profile': '/venue/analytics',
  '/venue/account-settings': '/venue/profile',
  '/venue/account-settings/personal': '/venue/account-settings',
  '/venue/account-settings/security': '/venue/account-settings',
  '/venue/account-settings/privacy': '/venue/account-settings',
  '/venue/account-settings/notifications': '/venue/account-settings',
  '/venue/settings': '/venue/profile',
  '/venue/settings/calendar': '/venue/settings',
  '/venue/settings/reviews': '/venue/settings',
  '/venue/settings/contact': '/venue/settings',
  '/venue/settings/hours': '/venue/settings',
  '/venue/settings/card': '/venue/settings',
  '/venue/settings/notifications': '/venue/settings',
  '/venue/settings/payments': '/venue/settings',
  '/venue/settings/payouts': '/venue/settings',
  '/venue/settings/payouts/connect-viva': '/venue/settings/payouts',
  '/venue/settings/payouts/connect-iban': '/venue/settings/payouts',
  '/venue/settings/payouts/callback': '/venue/settings/payouts',
  '/venue/settings/payouts/viva-info': '/venue/settings/payouts/connect-viva',
  '/venue/settings/photos': '/venue/settings',
  '/venue/settings/social': '/venue/settings',
  '/venue/events': '/venue/analytics',
  '/venue/events/new': '/venue/events',
  '/venue/events/:eventId/edit': '/venue/events',
  '/venue/awaiting/:txnId': '/venue/scan',
  '/venue/transaction/:txnId': '/venue/scan',
  // Venue onboarding wizard intentionally NOT mapped — those pages
  // own their own back logic (skip-aware, step-aware).

  // === STAFF (ADMIN ROLE) ===
  '/staff-admin/people': null,
  '/staff-admin/tips': '/staff-admin/people',
  '/staff-admin/calendar': '/staff-admin/people',
  '/staff-admin/scan': '/staff-admin/people',
  '/staff-admin/share': '/staff-admin/people',
  '/staff-admin/chat/:convoId': '/staff-admin/share',
  '/staff-admin/awaiting/:txnId': '/staff-admin/scan',
  '/staff-admin/transaction/:txnId': '/staff-admin/scan',
  '/staff-admin/settings': '/staff-admin/profile',
  '/staff-admin/settings/calendar': '/staff-admin/settings',
  '/staff-admin/settings/reviews': '/staff-admin/settings',
  '/staff-admin/settings/hours': '/staff-admin/settings',
  '/staff-admin/settings/photos': '/staff-admin/settings',
  '/staff-admin/settings/social': '/staff-admin/settings',
  '/staff-admin/events': '/staff-admin/people',
  '/staff-admin/events/new': '/staff-admin/events',
  '/staff-admin/events/:eventId/edit': '/staff-admin/events',
  '/staff-admin/profile': '/staff-admin/people',
  '/staff-admin/account-settings': '/staff-admin/profile',
  '/staff-admin/account-settings/personal': '/staff-admin/account-settings',
  '/staff-admin/account-settings/security': '/staff-admin/account-settings',
  '/staff-admin/account-settings/privacy': '/staff-admin/account-settings',
  '/staff-admin/account-settings/notifications': '/staff-admin/account-settings',

  // === STAFF (REGULAR) ===
  '/staff-app/people': null,
  '/staff-app/tips': '/staff-app/people',
  '/staff-app/scan': '/staff-app/people',
  '/staff-app/share': '/staff-app/people',
  '/staff-app/chat/:convoId': '/staff-app/share',
  '/staff-app/awaiting/:txnId': '/staff-app/scan',
  '/staff-app/transaction/:txnId': '/staff-app/scan',
  '/staff-app/profile': '/staff-app/people',
  '/staff-app/account-settings': '/staff-app/profile',
  '/staff-app/account-settings/personal': '/staff-app/account-settings',
  '/staff-app/account-settings/security': '/staff-app/account-settings',
  '/staff-app/account-settings/privacy': '/staff-app/account-settings',
  '/staff-app/account-settings/notifications': '/staff-app/account-settings',

  // === SUPPORT (role-aware: null → role home) ===
  '/support': null,
  '/support/contact': '/support',
  '/support/faq': '/support',
  '/support/report-venue': '/support',
  '/support/feedback': '/support',

  // === MISC AUTHENTICATED ===
  '/notifications': null,
  '/switch-role': null,

  // === LEGAL ===
  '/privacy': null,
  '/terms': null,
  '/delete-account': null,
  '/account-deleted': null,

  // === AUTH / ONBOARDING ENTRY ===
  '/login': '/welcome',
  '/register': '/select-role',
  '/select-role': '/welcome',
  '/welcome': null,
  '/about': '/welcome',
  '/contact': '/welcome',
  '/forgot-password': '/login',
  '/reset-password': '/login',
  '/two-factor': '/login',
  '/verify-email': '/login',
  '/guest-info': '/select-role',

  // === HOST SIGN-UP FUNNEL ===
  '/venue/intro': '/switch-role',
  '/host-intro-1': '/switch-role',
  '/host-intro-2': '/host-intro-1',
  '/host-verify-listing': '/host-intro-2',
  '/host-verify-details': '/host-verify-listing',
  '/host-pending': '/host-intro-1',
};

const getRoleHome = (user) => {
  if (!user) return '/welcome';
  const role = user.role;
  if (role === 'guest') return '/guest/home';
  if (role === 'host') return '/host/active-qr';
  if (role === 'venue_owner') return '/venue/analytics';
  if (role === 'staff') {
    return user.staff_role === 'admin' ? '/staff-admin/people' : '/staff-app/people';
  }
  if (role === 'admin') return '/admin/dashboard';
  return '/welcome';
};

const matchTemplate = (pathname) => {
  if (Object.prototype.hasOwnProperty.call(PARENTS, pathname)) return pathname;
  const parts = pathname.split('/');
  for (const key of Object.keys(PARENTS)) {
    if (!key.includes(':')) continue;
    const tpl = key.split('/');
    if (tpl.length !== parts.length) continue;
    let ok = true;
    for (let i = 0; i < tpl.length; i += 1) {
      if (tpl[i].startsWith(':')) continue;
      if (tpl[i] !== parts[i]) { ok = false; break; }
    }
    if (ok) return key;
  }
  return null;
};

/**
 * Returns the path the back arrow should navigate to from `pathname`.
 * Falls back to the user's role-home when the path isn't mapped.
 */
export const resolveBackPath = (pathname, user) => {
  const tpl = matchTemplate(pathname);
  if (tpl === null) return getRoleHome(user);
  const parent = PARENTS[tpl];
  if (parent === null) return getRoleHome(user);
  return parent;
};

export const __TEST_ONLY__ = { PARENTS, getRoleHome, matchTemplate };
